import type RealtimeKitClient from "@cloudflare/realtimekit";

type SelfWithSpeakerApi = RealtimeKitClient["self"] & {
    setupSpeaker?: () => Promise<void>;
    getSpeakerDevices?: () => Promise<MediaDeviceInfo[]>;
    getAudioDevices?: () => Promise<MediaDeviceInfo[]>;
    setSpeakerDevice?: (
        device: MediaDeviceInfo,
        opts: { saveDevicePreference: boolean },
    ) => Promise<void>;
    setDevice?: (device: MediaDeviceInfo) => Promise<void>;
    getCurrentDevices?: () => {
        audio?: MediaDeviceInfo;
        video?: MediaDeviceInfo;
        speaker?: MediaDeviceInfo;
    };
};

type MeetingWithAudioPlayback = RealtimeKitClient & {
    audio?: {
        playTracks?: (tracks: MediaStreamTrack[]) => Promise<void>;
        play?: () => Promise<void>;
        setSpeakerDevice?: (deviceId: string) => void;
    };
};

type AudioSessionNavigator = Navigator & {
    audioSession?: { type?: string };
};

export type MobileSpeakerOptions = {
    /**
     * Só com gesto do usuário (botão "Ouvir alto").
     * Faz kick de mic + Speakerphone Android + troca agressiva de sessão.
     * NUNCA usar em intervalo / visibility / participantJoined — isso pode
     * piorar quedas de conexão.
     */
    routeKick?: boolean;
    /** @deprecated Use `routeKick`. Mantido por compatibilidade. */
    iosMicPulse?: boolean;
    /**
     * Observador / só-ouvindo: força sessão `playback` (alto-falante no iOS),
     * mesmo se o SDK reportar estado ambíguo do mic.
     */
    listenOnly?: boolean;
    /**
     * O player oculto do SDK reatribui o `srcObject` a cada `play()`.
     * No intervalo isso derruba uma voz que já estava saindo.
     * Só religar ao voltar para a aba, ao entrar alguém ou no botão.
     */
    replayHiddenPlayer?: boolean;
    root?: ParentNode | null;
};

export type SpeakerBoostResult = {
    ok: boolean;
    mediaElements: number;
    played: number;
    audioSessionSet: boolean;
    speakerDeviceSet: boolean;
    sinkIdApplied: number;
    iosPulse: boolean;
    mirroredTracks: number;
    androidSpeakerMic: boolean;
    /** Mic/saída resetados para "padrão" (corrige fone fantasma). */
    defaultDeviceReset: boolean;
    /** Voz remota ligada num AudioContext — caminho que sai no alto-falante. */
    speakerGraph: boolean;
    /** Microfone republicado porque a faixa tinha morrido no toque. */
    micRepublished: boolean;
    message: string;
};

const MIRROR_HOST_ID = "cb-mobile-audio-mirror";
const IOS_UNLOCK_AUDIO_ID = "cb-ios-audio-unlock";
/** WAV mínimo. O iPhone só aceita o play() seguinte se um play() nasceu dentro do toque. */
const IOS_UNLOCK_WAV =
    "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";

/** Última leva de faixas já entregue ao player oculto. Evita `play()` repetido. */
let lastHiddenPlaybackKey = "";
/** Toque do usuário nesta página. Depois disso o aviso não pode cobrir a sala de novo. */
let userUnlockedPlayback = false;

/**
 * O botão "Ouvir alto" liga este grafo. A manutenção da sala só atualiza as
 * faixas — não recria dispositivo nem desliga o microfone.
 */
let speakerGraphWanted = false;
let speakerAudioContext: AudioContext | null = null;
let speakerGraphNodes: AudioNode[] = [];
let lastSpeakerTrackKey = "";
let lastMicRepublishAt = 0;

type WebkitWindow = Window & { webkitAudioContext?: typeof AudioContext };

function releaseSpeakerGraph(): void {
    speakerGraphWanted = false;
    lastSpeakerTrackKey = "";
    for (const node of speakerGraphNodes) {
        try {
            node.disconnect();
        } catch {
            // já desconectado
        }
    }
    speakerGraphNodes = [];
    if (speakerAudioContext && speakerAudioContext.state !== "closed") {
        void speakerAudioContext.close().catch(() => undefined);
    }
    speakerAudioContext = null;
}

function isSpeakerGraphRunning(): boolean {
    return speakerGraphWanted && speakerAudioContext?.state === "running";
}

export function remoteElementNeedsResume(args: {
    isMobile: boolean;
    inMirror: boolean;
    paused: boolean;
    ended: boolean;
    carriesRemoteAudio: boolean;
}): boolean {
    if (!args.carriesRemoteAudio && !args.inMirror) {
        return false;
    }
    if (!args.paused && !args.ended) {
        return false;
    }
    // No celular o shell fica mudo de propósito quando o player dedicado toca.
    // Retomar o shell aqui duplicaria a voz (auricular + alto-falante).
    if (args.isMobile && !args.inMirror) {
        return false;
    }
    return true;
}

/** Limpa o mirror global (usar no soft reconnect para não ficar com tracks mortas). */
export function clearMobileAudioMirror(): void {
    lastHiddenPlaybackKey = "";
    releaseSpeakerGraph();
    if (typeof document === "undefined") {
        return;
    }
    const host = document.getElementById(MIRROR_HOST_ID);
    if (!host) {
        return;
    }
    Array.from(host.querySelectorAll("audio")).forEach((el) => {
        const audio = el as HTMLAudioElement;
        try {
            audio.pause();
        } catch {
            // ignore
        }
        audio.srcObject = null;
        audio.remove();
    });
}

/**
 * Restaura áudio mobile com retries (caso típico: voltar do 2º plano / reconectar).
 * Sem routeKick por padrão — seguro para estabilidade.
 */
export function scheduleMobileAudioRestore(
    meetingClient: RealtimeKitClient | null,
    options: MobileSpeakerOptions & { delaysMs?: number[] } = {},
): () => void {
    if (!meetingClient || typeof window === "undefined") {
        return () => undefined;
    }

    const { delaysMs, ...speakerOptions } = options;
    const delays = delaysMs ?? [0, 400, 1200, 2800];
    const timers = delays.map((delay) =>
        window.setTimeout(() => {
            void ensureMobileSpeakerOutput(meetingClient, speakerOptions);
            boostRemoteMediaElements(speakerOptions.root);
        }, delay),
    );

    return () => {
        timers.forEach((id) => window.clearTimeout(id));
    };
}

/**
 * RTK renderiza `<audio>`/`<video>` dentro de Shadow DOM.
 * querySelectorAll no light DOM NÃO enxerga esses elementos — bug do boost antigo.
 */
export function collectMediaElementsDeep(root: ParentNode | null): HTMLMediaElement[] {
    if (!root) {
        return [];
    }

    const results: HTMLMediaElement[] = [];
    const seen = new Set<HTMLMediaElement>();

    const visit = (node: ParentNode) => {
        const list =
            typeof (node as ParentNode & { querySelectorAll?: typeof document.querySelectorAll })
                .querySelectorAll === "function"
                ? Array.from(
                      (node as Document | Element | ShadowRoot).querySelectorAll(
                          "audio, video",
                      ),
                  )
                : [];

        for (const el of list) {
            const media = el as HTMLMediaElement;
            if (!seen.has(media)) {
                seen.add(media);
                results.push(media);
            }
        }

        const all =
            typeof (node as ParentNode & { querySelectorAll?: typeof document.querySelectorAll })
                .querySelectorAll === "function"
                ? Array.from((node as Document | Element | ShadowRoot).querySelectorAll("*"))
                : [];

        for (const el of all) {
            if (el.shadowRoot) {
                visit(el.shadowRoot);
            }
        }
    };

    visit(root);
    return results;
}

function resolveBoostRoot(root?: ParentNode | null): ParentNode | null {
    if (typeof document === "undefined") {
        return null;
    }
    if (root instanceof HTMLElement || root instanceof Document || root instanceof ShadowRoot) {
        return root;
    }
    return (
        document.querySelector(".meeting-room-shell") ??
        document.querySelector("rtk-meeting") ??
        document.body
    );
}

function detectMobileOs(): "ios" | "android" | "other" {
    if (typeof navigator === "undefined") {
        return "other";
    }
    const ua = navigator.userAgent || "";
    if (/iPhone|iPad|iPod/i.test(ua)) {
        return "ios";
    }
    if (/Android/i.test(ua)) {
        return "android";
    }
    return "other";
}

/**
 * Sessão de áudio mobile (best practice LiveKit/Stream 2025–2026):
 * - Mic OFF / observador → `playback` (tende a alto-falante no iOS)
 * - Mic ON → `play-and-record` (necessário para WebRTC bidirecional)
 * - `aggressiveKick` só no toque do usuário (nunca em intervalo)
 *
 * Ref MDN: play-and-record pode rotear para o auricular.
 * Ref LiveKit RN #322: playback até precisar do mic.
 */
export function applyMobileAudioSession(options?: {
    preferLoudspeaker?: boolean;
    micEnabled?: boolean;
    /** Só no botão Ouvir alto — alterna tipos para forçar re-rota. */
    aggressiveKick?: boolean;
}): boolean {
    if (typeof navigator === "undefined") {
        return false;
    }
    const session = (navigator as AudioSessionNavigator).audioSession;
    if (!session || typeof session !== "object") {
        return false;
    }
    try {
        const micEnabled = options?.micEnabled === true;
        const aggressiveKick = options?.aggressiveKick === true;

        // Só-ouvindo: sempre playback (melhor chance de alto-falante).
        if (!micEnabled) {
            session.type = "playback";
            return true;
        }

        // Falando: play-and-record. Kick agressivo só sob gesto do usuário.
        if (aggressiveKick) {
            session.type = "auto";
            session.type = "play-and-record";
            return true;
        }

        session.type = "play-and-record";
        return true;
    } catch {
        return false;
    }
}

async function applySinkIdIfPossible(
    media: HTMLMediaElement,
    deviceId?: string,
): Promise<boolean> {
    const anyMedia = media as HTMLMediaElement & {
        setSinkId?: (id: string) => Promise<void>;
        sinkId?: string;
    };
    if (typeof anyMedia.setSinkId !== "function") {
        return false;
    }
    const candidates = deviceId
        ? [deviceId, "", "default"]
        : ["", "default"];
    for (const id of candidates) {
        try {
            await anyMedia.setSinkId(id);
            return true;
        } catch {
            // tenta próximo
        }
    }
    return false;
}

/**
 * Garante volume máximo e reprodução ativa nos elementos de mídia da sala
 * (incluindo Shadow DOM do RealtimeKit).
 */
export function boostRemoteMediaElements(root?: ParentNode | null): number {
    // O grafo do alto-falante já está tocando. Religar o shell devolve o
    // auricular e dobra a voz.
    if (isSpeakerGraphRunning()) {
        return 0;
    }
    const scope = resolveBoostRoot(root);
    const mediaElements = collectMediaElementsDeep(scope);
    let played = 0;

    for (const media of mediaElements) {
        media.volume = 1;
        media.muted = false;
        media.setAttribute("playsinline", "");
        media.setAttribute("webkit-playsinline", "true");
        if (media.paused) {
            void media
                .play()
                .then(() => {
                    played += 1;
                })
                .catch(() => undefined);
        } else {
            played += 1;
        }
        void applySinkIdIfPossible(media);
    }

    return mediaElements.length;
}

/**
 * Fone / Bluetooth de verdade.
 * "microfone" contém "fone" e o Chrome chama o mic embutido de "communications"
 * — tratar isso como fone fazia o app trocar o microfone sozinho e a pessoa
 * sumir para quem estava ouvindo.
 */
export function isExternalHeadsetDevice(label: string, deviceId: string): boolean {
    if (deviceId === "default" || deviceId === "communications") {
        return false;
    }
    const text = (label || "").toLowerCase();
    if (!text) {
        return false;
    }
    if (/microfone|microphone/.test(text) && !/headset|headphone|bluetooth|airpods|earpiece|auricular|fone de ouvido/.test(text)) {
        return false;
    }
    return /headset|headphone|bluetooth|airpods|ear\s?buds?|hands-?\s?free|usb audio|earpiece|auricular|fone de ouvido/.test(
        text,
    );
}

/** Faixa local publicada não está saindo. Mic desligado de propósito não conta. */
export function publishedMicIsUnhealthy(
    self: { audioEnabled?: boolean; audioTrack?: MediaStreamTrack | null } | null | undefined,
    options?: { includeMuted?: boolean },
): boolean {
    if (!self?.audioEnabled) {
        return false;
    }
    const track = self.audioTrack ?? null;
    if (!track) {
        return true;
    }
    if (track.readyState === "ended" || track.enabled === false) {
        return true;
    }
    return options?.includeMuted === true && track.muted === true;
}

/** Dispositivo “padrão” / embutido — o que a cliente corrigiu manualmente nas configs. */
function looksLikeDefaultOrBuiltin(label: string, deviceId: string): boolean {
    if (deviceId === "default") {
        return true;
    }
    const text = `${label}`.toLowerCase();
    return /default|padr[aã]o|built-?in|interno|microphone|microfone|phone mic/.test(text);
}

/**
 * No Android, o input "Speakerphone" é o que tira a voz do auricular.
 * "default" costuma continuar no fone de ouvido da chamada — por isso o
 * botão Ouvir alto não aumentava o som.
 */
export function pickLoudspeakerInput(
    inputs: MediaDeviceInfo[],
    mobileOs: "ios" | "android" | "other",
): MediaDeviceInfo | null {
    if (!inputs.length) {
        return null;
    }

    if (mobileOs === "android") {
        const speakerphone = inputs.find(
            (d) =>
                /speakerphone/i.test(d.label) &&
                !isExternalHeadsetDevice(d.label, d.deviceId),
        );
        if (speakerphone) {
            return speakerphone;
        }
    }

    return (
        inputs.find((d) => d.deviceId === "default") ??
        inputs.find((d) => d.deviceId === "communications") ??
        inputs.find(
            (d) =>
                looksLikeDefaultOrBuiltin(d.label, d.deviceId) &&
                !isExternalHeadsetDevice(d.label, d.deviceId),
        ) ??
        inputs.find((d) => !isExternalHeadsetDevice(d.label, d.deviceId)) ??
        null
    );
}

export function pickLoudspeakerOutput(speakers: MediaDeviceInfo[]): MediaDeviceInfo | null {
    if (!speakers.length) {
        return null;
    }

    const builtin = (d: MediaDeviceInfo) => !isExternalHeadsetDevice(d.label, d.deviceId);
    return (
        speakers.find((d) => builtin(d) && /speakerphone|alto-?falante|loudspeaker/i.test(d.label)) ??
        speakers.find((d) => builtin(d) && /\bspeaker\b/i.test(d.label)) ??
        speakers.find((d) => d.deviceId === "default") ??
        speakers.find((d) => builtin(d)) ??
        null
    );
}

/**
 * Troca mic/saída só no toque de "Ouvir alto".
 * No Android escolhe Speakerphone — é isso que tira a chamada do auricular.
 * Fora do toque não chama setDevice: a manutenção de 15s estava republicando
 * o microfone (rótulo "Microfone" casava com "fone") e a voz sumia para os outros.
 */
async function resetToPreferredAudioDevices(
    self: SelfWithSpeakerApi,
    options: { force?: boolean; mobileOs?: "ios" | "android" | "other" } = {},
): Promise<{ micReset: boolean; speakerReset: boolean; usedSpeakerphone: boolean }> {
    const result = {
        micReset: false,
        speakerReset: false,
        usedSpeakerphone: false,
    };

    if (options.force !== true) {
        return result;
    }

    const mobileOs = options.mobileOs ?? "other";

    try {
        const current = self.getCurrentDevices?.();
        const currentMic = current?.audio;

        if (typeof self.getAudioDevices === "function" && typeof self.setDevice === "function") {
            const inputs = await self.getAudioDevices();
            const preferred = pickLoudspeakerInput(inputs, mobileOs);
            const stuckOnHeadset =
                currentMic != null &&
                isExternalHeadsetDevice(currentMic.label || "", currentMic.deviceId || "");
            const wantsSpeakerphone =
                mobileOs === "android" &&
                preferred != null &&
                /speakerphone/i.test(preferred.label);

            if (
                preferred &&
                preferred.deviceId !== currentMic?.deviceId &&
                (wantsSpeakerphone || stuckOnHeadset || mobileOs !== "android")
            ) {
                await self.setDevice(preferred);
                result.micReset = true;
                result.usedSpeakerphone = wantsSpeakerphone;
            }
        }

        if (typeof self.getSpeakerDevices === "function" && typeof self.setSpeakerDevice === "function") {
            const speakers = await self.getSpeakerDevices();
            const preferredSpeaker = pickLoudspeakerOutput(speakers);
            const currentSpeaker = current?.speaker;
            if (
                preferredSpeaker &&
                preferredSpeaker.deviceId !== currentSpeaker?.deviceId
            ) {
                await self.setSpeakerDevice(preferredSpeaker, {
                    saveDevicePreference: false,
                });
                result.speakerReset = true;
            }
        }
    } catch {
        // ignore
    }

    return result;
}

async function applySpeakerDevice(self: SelfWithSpeakerApi): Promise<boolean> {
    if (typeof self.getSpeakerDevices !== "function" || typeof self.setSpeakerDevice !== "function") {
        return false;
    }

    const speakers = await self.getSpeakerDevices();
    const preferred = pickLoudspeakerOutput(speakers);
    if (!preferred) {
        return false;
    }

    const currentId = self.getCurrentDevices?.()?.speaker?.deviceId;
    if (currentId && currentId === preferred.deviceId) {
        return true;
    }

    await self.setSpeakerDevice(preferred, { saveDevicePreference: false });
    return true;
}

function collectRemoteAudioTracks(meetingClient: RealtimeKitClient): MediaStreamTrack[] {
    const tracks: MediaStreamTrack[] = [];
    const seen = new Set<string>();

    try {
        const joined = meetingClient.participants?.joined;
        const list =
            typeof joined?.toArray === "function"
                ? joined.toArray()
                : Array.from((joined as unknown as Map<string, { audioTrack?: MediaStreamTrack; audioEnabled?: boolean }>)?.values?.() ?? []);

        for (const peer of list) {
            const track = (peer as { audioTrack?: MediaStreamTrack; rawAudioTrack?: MediaStreamTrack })
                .audioTrack ?? (peer as { rawAudioTrack?: MediaStreamTrack }).rawAudioTrack;
            const enabled = (peer as { audioEnabled?: boolean }).audioEnabled !== false;
            if (!track || track.readyState === "ended" || !enabled) {
                continue;
            }
            if (seen.has(track.id)) {
                continue;
            }
            seen.add(track.id);
            tracks.push(track);
        }
    } catch {
        // ignore
    }

    return tracks;
}

function startMediaPlay(media: HTMLMediaElement): void {
    try {
        const pending = media.play();
        if (pending && typeof pending.catch === "function") {
            void pending.catch(() => undefined);
        }
    } catch {
        // autoplay ainda bloqueado
    }
}

/**
 * O iPhone descarta o toque se houver qualquer espera antes do play().
 * Esta função não pode usar await: ela roda no mesmo clique que o botão.
 */
export function unlockMediaPlaybackInGesture(
    meetingClient?: RealtimeKitClient | null,
): void {
    userUnlockedPlayback = true;
    if (typeof document === "undefined") {
        return;
    }

    if (speakerAudioContext && speakerAudioContext.state === "suspended") {
        void speakerAudioContext.resume().catch(() => undefined);
    }

    let primer = document.getElementById(IOS_UNLOCK_AUDIO_ID) as HTMLAudioElement | null;
    if (!primer) {
        primer = document.createElement("audio");
        primer.id = IOS_UNLOCK_AUDIO_ID;
        primer.setAttribute("playsinline", "");
        primer.setAttribute("webkit-playsinline", "true");
        primer.preload = "auto";
        primer.src = IOS_UNLOCK_WAV;
        primer.setAttribute("aria-hidden", "true");
        primer.style.cssText = "position:fixed;width:1px;height:1px;opacity:0.02;pointer-events:none;";
        document.body.appendChild(primer);
    }
    startMediaPlay(primer);

    const remoteIds = new Set(
        (meetingClient ? collectRemoteAudioTracks(meetingClient) : []).map((track) => track.id),
    );
    const selfTrackId = meetingClient?.self?.audioTrack?.id;

    for (const media of collectMediaElementsDeep(document.body)) {
        if (media === primer) {
            continue;
        }
        const carriesRemote = elementCarriesRemoteAudio(media, remoteIds, selfTrackId);
        if (carriesRemote) {
            media.muted = false;
            media.volume = 1;
        }
        media.setAttribute("playsinline", "");
        media.setAttribute("webkit-playsinline", "true");
        if (media.paused || media.ended) {
            startMediaPlay(media);
        }
    }

    if (!meetingClient) {
        return;
    }
    const meeting = meetingClient as MeetingWithAudioPlayback;
    const tracks = collectRemoteAudioTracks(meetingClient);
    if (tracks.length > 0 && typeof meeting.audio?.playTracks === "function") {
        const key = remoteTrackKey(tracks);
        void meeting.audio.playTracks(tracks).then(() => {
            lastHiddenPlaybackKey = key;
        }).catch(() => undefined);
        return;
    }
    if (typeof meeting.audio?.play === "function") {
        void meeting.audio.play().catch(() => undefined);
    }
}

export function hasUserUnlockedPlayback(): boolean {
    return userUnlockedPlayback;
}

export function resetMediaPlaybackUnlockForTests(): void {
    userUnlockedPlayback = false;
    if (typeof document !== "undefined") {
        document.getElementById(IOS_UNLOCK_AUDIO_ID)?.remove();
    }
}

function elementCarriesRemoteAudio(
    media: HTMLMediaElement,
    remoteIds: Set<string>,
    selfTrackId?: string,
): boolean {
    const stream = media.srcObject;
    if (typeof MediaStream === "undefined" || !(stream instanceof MediaStream)) {
        return false;
    }
    return stream.getAudioTracks().some((track) => {
        if (track.readyState !== "live") {
            return false;
        }
        if (remoteIds.has(track.id)) {
            return true;
        }
        // <audio> com faixa viva que não é o próprio microfone.
        return media instanceof HTMLAudioElement && track.id !== selfTrackId;
    });
}

/**
 * Retoma voz remota que parou de sair.
 * Não troca dispositivo, não mexe no microfone, não altera volume e não
 * reconecta. No celular não religa o shell (isso duplicaria o áudio).
 */
export async function resumePausedRemoteAudio(
    meetingClient: RealtimeKitClient | null,
): Promise<number> {
    if (!meetingClient || typeof document === "undefined") {
        return 0;
    }

    const tracks = collectRemoteAudioTracks(meetingClient);
    if (!tracks.length) {
        return 0;
    }

    const remoteIds = new Set(tracks.map((track) => track.id));
    const selfTrackId = meetingClient.self?.audioTrack?.id;
    const isMobile = detectMobileOs() !== "other";
    const mirror = document.getElementById(MIRROR_HOST_ID);
    const scope = resolveBoostRoot();
    const elements = collectMediaElementsDeep(scope);
    if (mirror) {
        elements.push(...collectMediaElementsDeep(mirror));
    }

    let resumed = 0;
    for (const media of elements) {
        const inMirror = Boolean(mirror && mirror.contains(media));
        const carriesRemoteAudio = elementCarriesRemoteAudio(media, remoteIds, selfTrackId);
        if (
            !remoteElementNeedsResume({
                isMobile,
                inMirror,
                paused: media.paused,
                ended: media.ended,
                carriesRemoteAudio,
            })
        ) {
            continue;
        }

        if (media.muted) {
            media.muted = false;
        }
        try {
            await media.play();
            resumed += 1;
        } catch {
            // autoplay ainda bloqueado — o toque na sala libera depois
        }
    }

    return resumed;
}

function ensureMirrorHost(): HTMLElement | null {
    if (typeof document === "undefined") {
        return null;
    }
    let host = document.getElementById(MIRROR_HOST_ID);
    if (!host) {
        host = document.createElement("div");
        host.id = MIRROR_HOST_ID;
        host.setAttribute("aria-hidden", "true");
        // Dentro da tela. Fora dela (left:-9999px) o Android Chrome não toca.
        host.style.cssText =
            "position:fixed;width:1px;height:1px;left:0;top:0;overflow:hidden;pointer-events:none;opacity:0.02;";
        document.body.appendChild(host);
    }
    return host;
}

/**
 * Espelha faixas remotas em `<audio>` no body.
 * No Android Chrome, elementos dedicados costumam sair no alto-falante
 * quando o player dentro do shell oculto fica no auricular.
 */
function remoteTrackKey(tracks: MediaStreamTrack[]): string {
    return tracks
        .map((track) => track.id)
        .sort()
        .join("|");
}

async function mirrorRemoteAudioToBody(
    meetingClient: RealtimeKitClient,
    options?: { replayHiddenPlayer?: boolean },
): Promise<number> {
    const tracks = collectRemoteAudioTracks(meetingClient);
    if (!tracks.length) {
        lastHiddenPlaybackKey = "";
        return 0;
    }

    const meeting = meetingClient as MeetingWithAudioPlayback;
    if (typeof meeting.audio?.playTracks === "function") {
        const key = remoteTrackKey(tracks);
        const replay = options?.replayHiddenPlayer !== false;
        // Mesmas faixas e sem pedido de replay: não chama play().
        // O SDK reatribui srcObject dentro de play() e a voz que já saía corta.
        if (!replay && key === lastHiddenPlaybackKey) {
            return tracks.length;
        }
        try {
            await meeting.audio.playTracks(tracks);
            lastHiddenPlaybackKey = key;
            return tracks.length;
        } catch {
            // cai no mirror manual
        }
    }

    const host = ensureMirrorHost();
    if (!host) {
        return 0;
    }

    const existing = new Map<string, HTMLAudioElement>();
    for (const el of Array.from(host.querySelectorAll("audio"))) {
        const id = el.dataset.trackId;
        if (id) {
            existing.set(id, el);
        }
    }

    const activeIds = new Set(tracks.map((t) => t.id));
    Array.from(existing.entries()).forEach(([id, el]) => {
        if (!activeIds.has(id)) {
            el.pause();
            el.srcObject = null;
            el.remove();
            existing.delete(id);
        }
    });

    let played = 0;
    for (const track of tracks) {
        let audio = existing.get(track.id);
        if (!audio) {
            audio = document.createElement("audio");
            audio.dataset.trackId = track.id;
            audio.autoplay = true;
            audio.setAttribute("playsinline", "");
            audio.setAttribute("webkit-playsinline", "true");
            host.appendChild(audio);
        }

        const stream = audio.srcObject instanceof MediaStream ? audio.srcObject : new MediaStream();
        const hasTrack = stream.getTracks().some((t) => t.id === track.id);
        if (!hasTrack) {
            stream.getTracks().forEach((t) => stream.removeTrack(t));
            stream.addTrack(track);
            audio.srcObject = stream;
        }

        audio.muted = false;
        audio.volume = 1;
        try {
            if (audio.paused) {
                await audio.play();
            }
            played += 1;
        } catch {
            // autoplay ainda bloqueado
        }
    }

    return played;
}

/**
 * Liga a voz remota num AudioContext. No Android/iOS o <audio> do WebRTC
 * fica no auricular (volume de chamada, baixo). O destino do AudioContext
 * sai no alto-falante, que é o que o botão "Ouvir alto" precisa fazer.
 * Não mexe no microfone.
 */
async function attachRemoteTracksToSpeaker(tracks: MediaStreamTrack[]): Promise<boolean> {
    if (typeof window === "undefined" || tracks.length === 0) {
        return false;
    }
    const AudioCtx = window.AudioContext || (window as WebkitWindow).webkitAudioContext;
    if (!AudioCtx) {
        return false;
    }

    const key = remoteTrackKey(tracks);
    if (isSpeakerGraphRunning() && key === lastSpeakerTrackKey) {
        return true;
    }

    try {
        if (!speakerAudioContext || speakerAudioContext.state === "closed") {
            speakerAudioContext = new AudioCtx();
        }
        for (const node of speakerGraphNodes) {
            try {
                node.disconnect();
            } catch {
                // já desconectado
            }
        }
        speakerGraphNodes = [];
        const source = speakerAudioContext.createMediaStreamSource(new MediaStream(tracks));
        const gain = speakerAudioContext.createGain();
        gain.gain.value = 1;
        source.connect(gain);
        gain.connect(speakerAudioContext.destination);
        speakerGraphNodes.push(source, gain);
        speakerGraphWanted = true;
        lastSpeakerTrackKey = key;
        if (speakerAudioContext.state === "suspended") {
            await speakerAudioContext.resume();
        }
        return speakerAudioContext.state === "running";
    } catch {
        return false;
    }
}

function muteElementsCarryingTracks(tracks: MediaStreamTrack[]): void {
    if (typeof document === "undefined" || tracks.length === 0) {
        return;
    }
    const ids = new Set(tracks.map((track) => track.id));
    for (const media of collectMediaElementsDeep(document.body)) {
        const stream = media.srcObject;
        if (!(stream instanceof MediaStream)) {
            continue;
        }
        if (stream.getAudioTracks().some((track) => ids.has(track.id))) {
            media.muted = true;
        }
    }
}

async function speakerOutputIdFromBrowser(): Promise<string | undefined> {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) {
        return undefined;
    }
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const outputs = devices.filter((device) => device.kind === "audiooutput");
        return pickLoudspeakerOutput(outputs)?.deviceId;
    } catch {
        return undefined;
    }
}

/**
 * Republica o microfone só quando a faixa publicada morreu.
 * O botão Ouvir alto não pode mais desligar o mic “para testar a rota”:
 * se o enableAudio seguinte falhava, Bruna/Dora paravam de ouvir.
 */
export async function republishMicrophoneIfUnhealthy(
    meetingClient: RealtimeKitClient | null,
    options?: { force?: boolean; includeMuted?: boolean },
): Promise<boolean> {
    const self = meetingClient?.self;
    if (!self?.audioEnabled) {
        return false;
    }
    if (!publishedMicIsUnhealthy(self, { includeMuted: options?.includeMuted === true })) {
        return false;
    }
    const now = Date.now();
    if (!options?.force && now - lastMicRepublishAt < 20000) {
        return false;
    }
    lastMicRepublishAt = now;
    try {
        await self.disableAudio();
        await self.enableAudio();
        return true;
    } catch {
        return false;
    }
}

/**
 * Kick de rota — SOMENTE após gesto do usuário.
 * Só ajusta a sessão de áudio. Não desliga o microfone e não abre outro
 * getUserMedia (os dois cortavam a voz de quem estava falando).
 */
async function mobileRouteKick(
    meetingClient: RealtimeKitClient,
    listenOnly: boolean,
): Promise<boolean> {
    const micEnabled = !listenOnly && Boolean(meetingClient.self?.audioEnabled);
    return applyMobileAudioSession({
        preferLoudspeaker: true,
        micEnabled,
        aggressiveKick: false,
    });
}

function flashVideoShellVisible(): () => void {
    if (typeof document === "undefined") {
        return () => undefined;
    }
    const shell = document.querySelector(".mobile-meeting-audio-shell") as HTMLElement | null;
    if (!shell) {
        return () => undefined;
    }
    const prevOpacity = shell.style.opacity;
    const prevPointer = shell.style.pointerEvents;
    shell.style.opacity = "0.02";
    shell.style.pointerEvents = "none";
    return () => {
        shell.style.opacity = prevOpacity;
        shell.style.pointerEvents = prevPointer;
    };
}

/**
 * No mobile, iOS/Android tendem a rotear WebRTC para o auricular.
 *
 * Caminho seguro (padrão): sessão playback/play-and-record + mirror + play.
 * Sem toggle de mic — seguro para intervalo / visibility (não piora quedas).
 *
 * Caminho com `routeKick: true`: só no botão "Ouvir alto" (gesto do usuário).
 */
export async function ensureMobileSpeakerOutput(
    meetingClient: RealtimeKitClient | null,
    options: MobileSpeakerOptions = {},
): Promise<SpeakerBoostResult> {
    const empty: SpeakerBoostResult = {
        ok: false,
        mediaElements: 0,
        played: 0,
        audioSessionSet: false,
        speakerDeviceSet: false,
        sinkIdApplied: 0,
        iosPulse: false,
        mirroredTracks: 0,
        androidSpeakerMic: false,
        defaultDeviceReset: false,
        speakerGraph: false,
        micRepublished: false,
        message: "Cliente de reunião indisponível",
    };

    if (!meetingClient) {
        return empty;
    }

    const routeKick = options.routeKick === true || options.iosMicPulse === true;
    // Antes de qualquer await. No iPhone o play() depois de uma espera é recusado.
    if (routeKick) {
        unlockMediaPlaybackInGesture(meetingClient);
    }
    const mobileOs = detectMobileOs();
    const self = meetingClient.self as SelfWithSpeakerApi;
    const micActuallyOn = Boolean(meetingClient.self?.audioEnabled);
    // Observador / listenOnly: trata como mic off → playback (best practice).
    const listenOnly = options.listenOnly === true || !micActuallyOn;
    const micEnabled = !listenOnly && micActuallyOn;
    const restoreShell = routeKick ? flashVideoShellVisible() : () => undefined;

    const audioSessionSet = applyMobileAudioSession({
        preferLoudspeaker: true,
        micEnabled,
        aggressiveKick: false,
    });

    let speakerDeviceSet = false;
    let androidSpeakerMic = false;
    let defaultDeviceReset = false;
    let iosPulse = false;

    // Troca de dispositivo só no toque. No intervalo, setDevice republicava o
    // microfone e quem estava do outro lado parava de ouvir.
    if (routeKick) {
        iosPulse = await mobileRouteKick(meetingClient, listenOnly);
        try {
            const deviceFix = await resetToPreferredAudioDevices(self, {
                force: true,
                mobileOs,
            });
            defaultDeviceReset = deviceFix.micReset || deviceFix.speakerReset;
            androidSpeakerMic = deviceFix.usedSpeakerphone;

            if (typeof self.setupSpeaker === "function") {
                await self.setupSpeaker();
                speakerDeviceSet = true;
            } else {
                speakerDeviceSet = await applySpeakerDevice(self);
            }
        } catch {
            try {
                speakerDeviceSet = await applySpeakerDevice(self);
            } catch {
                speakerDeviceSet = false;
            }
        }
    }

    const browserSpeakerId = routeKick ? await speakerOutputIdFromBrowser() : undefined;
    const speakerId = browserSpeakerId || self.getCurrentDevices?.()?.speaker?.deviceId;

    const scope = resolveBoostRoot(options.root);
    const mediaElements = collectMediaElementsDeep(scope);

    if (routeKick) {
        speakerGraphWanted = true;
    }

    let played = 0;
    let sinkIdApplied = 0;

    for (const media of mediaElements) {
        media.volume = 1;
        if (!speakerGraphWanted) {
            media.muted = false;
        }
        media.setAttribute("playsinline", "");
        media.setAttribute("webkit-playsinline", "true");
        try {
            if (media.paused) {
                await media.play();
            }
            played += 1;
        } catch {
            // autoplay ainda bloqueado — o toque do botão libera
        }
        if (speakerId && (await applySinkIdIfPossible(media, speakerId))) {
            sinkIdApplied += 1;
        }
    }

    const remoteTracks = collectRemoteAudioTracks(meetingClient);
    let speakerGraph = false;
    if (speakerGraphWanted && remoteTracks.length > 0) {
        speakerGraph = await attachRemoteTracksToSpeaker(remoteTracks);
    }

    let mirroredTracks = 0;
    if (speakerGraph) {
        // O grafo está no alto-falante. Cala o player do WebRTC para não
        // deixar a mesma voz no auricular.
        muteElementsCarryingTracks(remoteTracks);
        mirroredTracks = remoteTracks.length;
    } else {
        for (const media of mediaElements) {
            media.muted = false;
            media.volume = 1;
        }
        try {
            mirroredTracks = await mirrorRemoteAudioToBody(meetingClient, {
                replayHiddenPlayer: options.replayHiddenPlayer,
            });
        } catch {
            mirroredTracks = 0;
        }
    }

    let micRepublished = false;
    if (routeKick && micActuallyOn) {
        await new Promise((resolve) => window.setTimeout(resolve, 300));
        if (publishedMicIsUnhealthy(meetingClient.self, { includeMuted: true })) {
            micRepublished = await republishMicrophoneIfUnhealthy(meetingClient, {
                force: true,
                includeMuted: true,
            });
        }
    }

    restoreShell();

    const found = Math.max(mediaElements.length, collectMediaElementsDeep(scope).length);
    const ok =
        speakerGraph ||
        mirroredTracks > 0 ||
        played > 0 ||
        sinkIdApplied > 0 ||
        androidSpeakerMic ||
        defaultDeviceReset ||
        iosPulse ||
        speakerDeviceSet;

    let message: string;
    if (speakerGraph || androidSpeakerMic) {
        message = micRepublished
            ? "Som no alto-falante e microfone religado. Se ainda estiver baixo, use os botões de volume na lateral do celular."
            : "Som no alto-falante. Se ainda estiver baixo, use os botões de volume na lateral do celular.";
    } else if (defaultDeviceReset) {
        message =
            "Saída de áudio atualizada. Se ainda estiver baixo, use os botões de volume na lateral do celular.";
    } else if (played > 0 || mirroredTracks > 0) {
        message =
            "Reprodução retomada. Se continuar baixo, toque de novo em Ouvir alto e aumente o volume na lateral do celular.";
    } else if (found === 0 && mirroredTracks === 0) {
        message =
            "Não achei o áudio dos outros ainda. Abra a aba Videochamada e toque de novo em Ouvir alto.";
    } else {
        message =
            "O celular segurou o áudio no auricular. Toque de novo em Ouvir alto e aumente o volume na lateral.";
    }

    return {
        ok,
        mediaElements: found,
        played,
        audioSessionSet,
        speakerDeviceSet,
        sinkIdApplied,
        iosPulse,
        mirroredTracks,
        androidSpeakerMic,
        defaultDeviceReset,
        speakerGraph,
        micRepublished,
        message,
    };
}
